import { useRef, useLayoutEffect, useEffect, useState } from "react";
import {
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  ReferenceLine,
} from "recharts";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import "./InvoicePage.scss";
import { useDarkModeCheck } from "../DashboardLayout";
import FilterRow from "../../../component/filter-row/FilterRow";
import {
  base64ToBlob,
  inputDateFormat,
  previousMonthRangeSelect,
  retrieveFileType,
  sortTableFunction,
} from "../../../service/utils/functions";
import Container from "../../../component/container/Container";
import DropdownSelect from "../../../component/dropdown-select/DropdownSelect";
import Button from "../../../component/button/Button";
import {
  invoiceTableHeader,
  invoiceTypeSelectorList,
  SortOrder,
} from "../../../service/utils/constants";
import SortableTable, {
  SortSettings,
} from "../../../component/sortable-table/SortableTable";
import {
  DOWNLOAD_DOCUMENT_FILE,
  GET_CUSTOMER_INVOICE_CHART,
  GET_INVOICE_DATA,
} from "../../../service/graphql/query";
import { InvoiceInput } from "../../../service/utils/interfaces";
import {
  invoiceChartDataFormat,
  invoiceDataFormat,
} from "../../../service/utils/parsers";
import { logout } from "../../../service/graphql/utils";
import * as Storage from "../../../service/storage";
import { trackDownload } from "../../../service/matomo";
import {
  useCustomerSelection,
  useRequestGuard,
} from "../../../service/hooks/customerData";

const InvoicePage = () => {
  const navigate = useNavigate();
  const { darkMode, userCustomers } = useDarkModeCheck();

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() =>
    previousMonthRangeSelect()
  );
  const [isDateRangeReady, setIsDateRangeReady] = useState(false);
  const [startDate, endDate] = dateRange;

  const [headerLine, setHeaderLine] = useState<any[]>([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [reordering, setReordering] = useState(false);
  const [searchParam, setSearchParam] = useState("");

  const [canViewPage, setCanViewPage] = useState(false);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<any>();
  const [selectedInvoiceCode, setSelectedInvoiceCode] = useState<any>();
  const [invoicesList, setInvoicesList] = useState<any[]>([]);
  const [invoiceGraphData, setInvoiceGraphData] = useState<any[]>([]);
  const [invoiceTableData, setInvoiceTableData] = useState<any[][]>([]);
  const [invoiceListResetting, setInvoiceListResetting] = useState(false);

  const { customerList, selectedCustomer, setSelectedCustomer } =
    useCustomerSelection(canViewPage ? userCustomers : undefined);
  const isNewInvoiceRequest = useRequestGuard();
  const [getInvoiceData, { data: invoiceRes }] = useLazyQuery(GET_INVOICE_DATA);
  const [getInvoiceChart, { data: invoiceChartRes }] = useLazyQuery(
    GET_CUSTOMER_INVOICE_CHART
  );
  const [downloadDocFile] = useLazyQuery(DOWNLOAD_DOCUMENT_FILE);

  useLayoutEffect(() => {
    if (containerRef && containerRef.current) {
      const width = containerRef.current.clientWidth;
      setWidth(width);
    }
  }, []);

  useEffect(() => {
    if (!userCustomers) return;
    const user = userCustomers[0];
    if (user) {
      if (user.category === "Tipping" || !user.pageAuth.financialPage) {
        window.history.replaceState(null, "", "/");
        navigate("/dashboard/home");
      } else setCanViewPage(true);
    } else logout(navigate);
  }, [userCustomers, navigate]);

  useEffect(() => {
    Storage.FILTER_DATA.get().then((filter) => {
      const start =
        filter.startDate.length > 0 ? new Date(filter.startDate) : null;
      const end = filter.endDate.length > 0 ? new Date(filter.endDate) : null;
      if (start && end) setDateRange([start, end]);
      else {
        const updateRange = previousMonthRangeSelect();
        setDateRange(updateRange);
      }
      setIsDateRangeReady(true);
    });

    setHeaderLine(invoiceTableHeader);

    const handleWindowResize = () => {
      if (containerRef && containerRef.current) {
        const width = containerRef.current.clientWidth;
        setWidth(width);
      }
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  // Invoices are fetched for the selected customer only. The guard skips the
  // repeated call when the filter row re-sends an unchanged date range.
  useEffect(() => {
    if (!isDateRangeReady || !selectedCustomer?.value) return;
    if (dateRange && dateRange[0] && dateRange[1]) {
      let tempInvoiceTypeFilter = ["Invoice", "Credit Note"];
      if (selectedInvoiceType && selectedInvoiceType.length > 0)
        tempInvoiceTypeFilter = selectedInvoiceType.map(
          (type: any) => type.label
        );
      const invoiceInputReq: InvoiceInput = {
        startDate: inputDateFormat(dateRange[0]),
        endDate: inputDateFormat(dateRange[1]),
        invoiceTypeList: tempInvoiceTypeFilter,
      };
      const variables = {
        customerId: selectedCustomer.value,
        input: invoiceInputReq,
      };
      if (!isNewInvoiceRequest(variables)) return;
      getInvoiceData({
        variables,
        context: { apiName: "middleware" },
      });
    }
  }, [
    isDateRangeReady,
    selectedCustomer,
    dateRange,
    selectedInvoiceType,
    getInvoiceData,
    isNewInvoiceRequest,
  ]);

  useEffect(() => {
    const customerInvoices = invoiceRes?.getCustomers?.[0];
    if (!customerInvoices) return;
    const [formatted] = invoiceDataFormat([customerInvoices] as any);
    setInvoicesList(formatted.invoicesCodes);
    setInvoiceTableData(formatted.invoices);
  }, [invoiceRes]);

  useEffect(() => {
    if (selectedCustomer) {
      resetInvoiceCodeSelector();
      setInvoicesList([]);
      setInvoiceTableData([]);
      getInvoiceChart({
        context: { apiName: "middleware" },
        variables: { customerId: selectedCustomer.value },
      });
    }
  }, [selectedCustomer, getInvoiceChart]);

  useEffect(() => {
    if (invoiceChartRes && invoiceChartRes.getInvoiceChart) {
      manageInvoiceChartData(invoiceChartRes);
    }
  }, [invoiceChartRes]);

  const manageInvoiceChartData = (invoiceChartRes: any) => {
    const tempInvoiceGraphData = invoiceChartDataFormat(
      invoiceChartRes.getInvoiceChart
    );
    setInvoiceGraphData(tempInvoiceGraphData);
  };

  const resetInvoiceCodeSelector = () => {
    setSelectedInvoiceCode(null);
    setInvoiceListResetting(true);
  };

  const changeSortAction = (col: number) => {
    setSortSettings({
      column: col,
      order:
        sortSettings?.column === col &&
        sortSettings?.order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending,
    });
    setReordering(true);
  };

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      const reorderedTable = invoiceTableData.sort((a, b) =>
        sortTableFunction(a, b, sortSettings)
      );
      setInvoiceTableData(reorderedTable);
    }
  }, [sortSettings, reordering, invoiceTableData]);

  const openFile = async (codeObj: any) => {
    const downloadLink = codeObj?.value;
    const fileName = codeObj?.label;
    const fileExtension = downloadLink?.split(".").pop();
    if (!codeObj?.value || !fileName || !fileExtension) return;
    const docFileRes = await downloadDocFile({
      variables: {
        fileId: downloadLink,
        source: "purgo",
      },
      context: { apiName: "middleware" },
    });
    if (!docFileRes.data?.downloadProtectedDocument) return;
    const fileType = retrieveFileType(fileExtension);
    const blob = base64ToBlob(
      docFileRes.data.downloadProtectedDocument,
      fileType
    );
    const file = new File([blob], `${fileName}.${fileExtension}`, {
      type: fileType,
    });
    trackDownload(file.name, "invoice-opened");
    const url = URL.createObjectURL(file);
    const win = window.open(url, "_blank");
    if (win) win.focus();
  };

  const changeSearchParam = (val: string) => {
    setSearchParam(val);
  };

  const resetTable = () => {
    changeSearchParam("");
    setSortSettings(undefined);
  };

  return (
    <div
      ref={containerRef}
      className={"invoice-wrapper" + (darkMode ? " dark" : "")}
    >
      <div className="filter-row row">
        <FilterRow
          width={width}
          customerData={customerList}
          onChangeCustomerData={(val) => {
            resetTable();
            setSelectedCustomer(val);
          }}
          invoiceTypeData={invoiceTypeSelectorList}
          onChangeInvoiceTypeData={(val) => {
            resetTable();
            setSelectedInvoiceType([...val]);
          }}
          displayDateRange
          startDate={startDate}
          endDate={endDate}
          onChangeTimeRange={(update) => {
            resetTable();
            setDateRange(update);
          }}
          noSideButton
          darkMode={darkMode}
        />
      </div>
      {invoiceGraphData && invoiceTableData && (
        <Container darkTheme={darkMode}>
          <div className="column">
            <div className="row">
              <div
                className={"text" + (darkMode ? " dark" : "")}
                style={{ fontSize: "var(--medium-font)" }}
              >
                View an invoice or credit note by selecting the document number
                from the drop-down list:
              </div>
            </div>
            {invoicesList && invoicesList.length > 0 && (
              <div className="row">
                <DropdownSelect
                  width={width}
                  title="invoice"
                  all="Select Invoice..."
                  data={invoicesList}
                  onChange={(val: any) => setSelectedInvoiceCode(val)}
                  resetting={invoiceListResetting}
                  onResetEnded={() => setInvoiceListResetting(false)}
                  darkMode={darkMode}
                />
                <Button
                  title="Fetch"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    fontSize: 12,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  onClick={() => openFile(selectedInvoiceCode)}
                />
              </div>
            )}
            {invoiceGraphData.length > 0 && (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  height={180}
                  data={invoiceGraphData}
                  style={{ cursor: "pointer" }}
                >
                  <XAxis
                    dataKey="name"
                    tick={{ fill: darkMode ? "#fff" : "#666", fontSize: 15.26 }}
                  />
                  <YAxis
                    tick={{ fill: darkMode ? "#fff" : "#666", fontSize: 15.26 }}
                  />
                  <ReferenceLine y={0} stroke={darkMode ? "#fff" : "#666"} />
                  <Tooltip
                    cursor={false}
                    wrapperClassName={
                      "tooltip-wrapper" + (darkMode ? " dark" : "")
                    }
                  />
                  <Legend />
                  <Bar
                    dataKey="invoice"
                    name="Invoice Amount"
                    fill={darkMode ? "#385ede" : "#121e46"}
                  />
                  <Bar dataKey="credit" name="Credited Amount" fill="#808080" />
                </BarChart>
              </ResponsiveContainer>
            )}
            <SortableTable
              id="invoices"
              width={width}
              header={headerLine}
              data={invoiceTableData}
              standaloneTable
              currentSort={sortSettings}
              onSortChanged={changeSortAction}
              currentFilterKey={searchParam}
              onFilterChanged={changeSearchParam}
              rowsPerPage={25}
              darkMode={darkMode}
              sourceDownload="purgo"
            />
          </div>
        </Container>
      )}
    </div>
  );
};

export default InvoicePage;
